import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { ticketsAPI, systemAPI, usersAPI, companiesAPI } from '../../services/api';
import { TicketFilters } from '../tickets/TicketFilters';
import { TicketTable } from '../tickets/TicketTable';
import { MessageDialog } from '../tickets/TicketDialogs/MessageDialog';
import { DetailViewDialog } from '../tickets/TicketDialogs/DetailViewDialog';
import { EditTicketDialog } from '../tickets/TicketDialogs/EditTicketDialog';
import { NewTicketDialog } from '../tickets/TicketDialogs/NewTicketDialog';
import { EditDialog } from '../tickets/TicketDialogs/EditDialog';
import { DeleteDialog } from '../tickets/TicketDialogs/DeleteDialog';
import { SuccessDialog } from '../tickets/TicketDialogs/SuccessDialog';
import { UpdateSuccessDialog } from '../tickets/TicketDialogs/UpdateSuccessDialog';
import { FilePreviewDialog } from '../tickets/TicketDialogs/FilePreviewDialog';
import { useTicketFilters } from '../../hooks/useTicketFilters'; 

const TicketList: React.FC = () => {
  const { user: authUser } = useAuth();
  const [tickets, setTickets] = useState<any[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [modules, setModules] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [priorities, setPriorities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [newTicketDialogOpen, setNewTicketDialogOpen] = useState(false);
  const [editTicketDialogOpen, setEditTicketDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);
  const [updateSuccessDialog, setUpdateSuccessDialog] = useState(false);
  
  // Selection states
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [ticketFiles, setTicketFiles] = useState<any[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  
  // Message states
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [messageFile, setMessageFile] = useState<File | null>(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  const { filters, setFilters, defaultStart, defaultEnd } = useTicketFilters();

  // Form states
  const [newTicketForm, setNewTicketForm] = useState({
    company_id: '',
    module_id: '',
    subject: '',
    description: '',
    email: '',
    priority_id: '1',
  });
  const [editForm, setEditForm] = useState({
    status_id: '',
    priority_id: '',
    assigned_to: '',
    notes: '',
  });
  const [editTicketForm, setEditTicketForm] = useState({
    module_id: '',
    subject: '',
    description: '',
    email: '',
    priority_id: '',
    mail_content: '',
  });
  const [files, setFiles] = useState<File[]>([]);
  const [mailContent, setMailContent] = useState('');
  
  // Notification states
  const [unreadMessagesCounts, setUnreadMessagesCounts] = useState<{[key: number]: number}>({});
  const [notificationLoading, setNotificationLoading] = useState(false);
  
  // File preview states
  const [previewFile, setPreviewFile] = useState<any>({
    open: false,
    file: null,
    type: 'other',
    blobUrl: null
  });
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning'
  });

  // Load initial data - DÜZELTİLDİ
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Lookup verilerini doğru API'lerden al
      const [modulesRes, statusesRes, prioritiesRes] = await Promise.all([
        systemAPI.getModules(),
        systemAPI.getStatuses(),
        systemAPI.getPriorities()
      ]);

      setModules(modulesRes.data);
      setStatuses(statusesRes.data);
      setPriorities(prioritiesRes.data);

      if (authUser?.role === 'admin' || authUser?.role === 'support') {
        try {
          const [companiesRes, usersRes] = await Promise.all([
            companiesAPI.getCompanies(),
            usersAPI.getSupportUsers()
          ]);
          setCompanies(companiesRes.data);
          setUsers(usersRes.data);
        } catch (error) {
          console.error('Admin data loading error:', error);
        }
      }

      if (authUser?.role === 'support') {
        try {
          const usersRes = await usersAPI.getSupportUsers();
          setUsers(usersRes.data);
        } catch (error) {
          console.error('Support users loading error:', error);
        }
      }

      // Load initial tickets
      const initialParams = { status: '1,2,3,4,5,7' };
      const response = await ticketsAPI.getTickets(initialParams);
      setTickets(response.data);
      setFilteredTickets(response.data);

    } catch (error: any) {
      console.error('Data loading error:', error);
      showSnackbar('Veriler yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  // Load filtered tickets
const loadFilteredTickets = useCallback(async () => {
  try {
    setLoading(true);
    const params: any = {};
    
    if (filters.status.length > 0) params.status = filters.status.join(',');
    if (filters.module.length > 0) params.module = filters.module.join(',');
    if (filters.priority.length > 0) params.priority = filters.priority.join(',');
    if (filters.assigned_to.length > 0) params.assigned_to = filters.assigned_to.join(',');
    if (filters.company.length > 0) params.company = filters.company.join(',');
    
    if (filters.search) {
      params.search = filters.search;
    }
    
    if (filters.id_search) {
      params.id_search = filters.id_search;
    }
    
    if (filters.dateRangeCreated.start && filters.dateRangeCreated.end) {
      params.start_date_created = filters.dateRangeCreated.start;
      params.end_date_created = filters.dateRangeCreated.end;
    }
    if (filters.dateRangeUpdated.start && filters.dateRangeUpdated.end) {
      params.start_date_updated = filters.dateRangeUpdated.start;
      params.end_date_updated = filters.dateRangeUpdated.end;
    }

    console.log('API Params:', params);
    const response = await ticketsAPI.getTickets(params);
    setTickets(response.data);
    setFilteredTickets(response.data);
    
  } catch (error: any) {
    console.error('Filtered tickets loading error:', error);
    showSnackbar('Talepler yüklenirken hata oluştu', 'error');
  } finally {
    setLoading(false);
  }
}, [filters]);

  // Load unread messages counts
  const loadUnreadMessagesCounts = useCallback(async () => {
    try {
      setNotificationLoading(true);
      const response = await ticketsAPI.getAllUnreadMessagesCounts();
      setUnreadMessagesCounts(response.data);
    } catch (error) {
      console.error('Load unread counts error:', error);
    } finally {
      setNotificationLoading(false);
    }
  }, []);

  // Mark messages as read
  const markMessagesAsRead = useCallback(async (ticketId: number) => {
    try {
      await ticketsAPI.markMessagesAsRead(ticketId);
      setUnreadMessagesCounts(prev => ({
        ...prev,
        [ticketId]: 0
      }));
    } catch (error) {
      console.error('Mark messages as read error:', error);
    }
  }, []);

  // Load messages
  const loadMessages = async (ticketId: number) => {
    try {
      setMessagesLoading(true);
      const response = await ticketsAPI.getMessages(ticketId);
      setMessages(response.data);
    } catch (error: any) {
      console.error('Load messages error:', error);
      showSnackbar('Mesajlar yüklenirken hata oluştu', 'error');
    } finally {
      setMessagesLoading(false);
    }
  };

  // Load ticket files
  const loadTicketFiles = async (ticketId: number) => {
    try {
      setFilesLoading(true);
      const { data } = await ticketsAPI.getFiles(ticketId);
      setTicketFiles(data || []);
    } catch (e) {
      console.error('Files load error:', e);
      setTicketFiles([]);
    } finally {
      setFilesLoading(false);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() && !messageFile) {
      showSnackbar('Mesaj veya dosya ekleyin', 'warning');
      return;
    }

    try {
      setSendingMessage(true);

      if (messageFile) {
        const formData = new FormData();
        formData.append('file', messageFile);
        if (newMessage.trim()) {
          formData.append('message', newMessage);
        }
        
        const messageResponse = await ticketsAPI.sendMessageWithFile(selectedTicket.id, formData);
        setMessages(prev => [...prev, messageResponse.data]);
      } else {
        const messageResponse = await ticketsAPI.sendMessage(selectedTicket.id, {
          message: newMessage,
          is_internal: false
        });
        setMessages(prev => [...prev, messageResponse.data]);
      }

      setNewMessage('');
      setMessageFile(null);
      showSnackbar('Mesaj gönderildi', 'success');
    } catch (error: any) {
      console.error('Send message error:', error);
      showSnackbar('Mesaj gönderilirken hata oluştu', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  // File handlers
  const handleMessageFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      if (file.size > 10 * 1024 * 1024) {
        showSnackbar('Dosya çok büyük (max 10MB)', 'error');
        return;
      }

      setMessageFile(file);
    }
  };

  const handleRemoveMessageFile = () => {
    setMessageFile(null);
  };

  const handleDownloadMessageFile = async (message: any) => {
    if (!message.file_path) return;
    
    try {
      const res = await ticketsAPI.downloadMessageFile(message.id);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = message.file_name || 'dosya';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar('Dosya indirildi', 'success');
    } catch (e) {
      console.error('Message file download error:', e);
      showSnackbar('Dosya indirilemedi', 'error');
    }
  };

  const handleFilePreview = async (file: any) => {
    try {
      setPreviewLoading(true);
      const response = await ticketsAPI.downloadFile(file.id);
      
      if (!response.data) {
        throw new Error('Dosya verisi alınamadı');
      }

      const mimeType = getMimeType(file.name);
      const blob = new Blob([response.data], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      const determinedFileType = getFileType(file.name);

      setPreviewFile({
        open: true,
        file: file,
        type: determinedFileType,
        blobUrl: blobUrl
      });

    } catch (error: any) {
      console.error('File preview error:', error);
      showSnackbar('Dosya açılamadı', 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleFileDownload = async (file: any) => {
    try {
      const res = await ticketsAPI.downloadFile(file.id);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const decodedFileName = decodeFileName(file.name || 'dosya');
      const cleanFileName = decodedFileName.replace(/[<>:"/\\|?*]/g, '_');
      
      a.download = cleanFileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar(`${cleanFileName} indirildi`, 'success');
    } catch (e) {
      console.error('File download error:', e);
      showSnackbar('Dosya indirilemedi', 'error');
    }
  };

  const handlePreviewClose = () => {
    if (previewFile.blobUrl) {
      URL.revokeObjectURL(previewFile.blobUrl);
    }
    setPreviewFile({ open: false, file: null, type: 'other', blobUrl: null });
  };

  const handleFileDelete = async (fileId: number) => {
    try {
      await ticketsAPI.deleteFile(fileId);
      showSnackbar('Dosya silindi', 'success');
      // Silinen dosyayı listeden kaldır
      setTicketFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (error: any) {
      console.error('❌ File delete error:', error);
      showSnackbar('Dosya silinemedi', 'error');
    }
};
  // File type helpers
  const getMimeType = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const mimeTypes: { [key: string]: string } = {
      'jpg': 'image/jpeg', 'jpeg': 'image/jpeg', 'png': 'image/png', 'gif': 'image/gif',
      'bmp': 'image/bmp', 'svg': 'image/svg+xml', 'webp': 'image/webp',
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'txt': 'text/plain', 'csv': 'text/csv',
      'zip': 'application/zip', 'rar': 'application/x-rar-compressed',
      '7z': 'application/x-7z-compressed'
    };
    
    return mimeTypes[ext || ''] || 'application/octet-stream';
  };

  const getFileType = (fileName: string): 'image' | 'pdf' | 'office' | 'text' | 'other' => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext || '')) {
      return 'image';
    } else if (ext === 'pdf') {
      return 'pdf';
    } else if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext || '')) {
      return 'office';
    } else if (['txt', 'csv'].includes(ext || '')) {
      return 'text';
    } else {
      return 'other';
    }
  };

  const decodeFileName = (fileName: string): string => {
    try {
      if (!fileName) return 'dosya';
      
      try {
        if (fileName.match(/^[A-Za-z0-9+/]+={0,2}$/) && fileName.length % 4 === 0) {
          const decoded = atob(fileName);
          if (decoded) return decoded;
        }
      } catch (e) {}

      try {
        const decoded = decodeURIComponent(escape(fileName));
        if (decoded !== fileName) return decoded;
      } catch (e) {}

      let fixedName = fileName
        .replace(/Ã/g, 'Ç').replace(/Ã§/g, 'ç')
        .replace(/Ä/g, 'Ğ').replace(/Ä/g, 'ğ')
        .replace(/Ã/g, 'Ü').replace(/Ã¼/g, 'ü')
        .replace(/Å/g, 'Ş').replace(/Å/g, 'ş')
        .replace(/Ã/g, 'İ').replace(/Ã¶/g, 'ö')
        .replace(/Ã/g, 'Ö').replace(/Ã½/g, 'ı')
        .replace(/Ã/g, 'I').replace(/Ã°/g, 'İ')
        .replace(/â/g, '')
        .replace(/â¦/g, '')
        .replace(/â/g, '')
        .replace(//g, '')
        .replace(//g, 'ş')
        .replace(//g, 'Ş')
        .replace(//g, '')
        .replace(//g, 'ü')
        .replace(//g, 'İ')
        .replace(//g, 'ş')
        .replace(//g, 'Ş');

      if (/[ÃÅ]/.test(fixedName)) {
        fixedName = fixedName
          .replace(/[^\x00-\x7F]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      }

      return fixedName || 'dosya';
    } catch (error) {
      return fileName || 'dosya';
    }
  };

  // Show snackbar
  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'warning' = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Ticket actions
  const handleView = (ticket: any) => {
    setSelectedTicket(ticket);
    setViewDialogOpen(true);
    loadTicketFiles(ticket.id);
  };

const handleOpenMessages = async (ticket: any) => {
  setSelectedTicket(ticket);
  setMessageDialogOpen(true);
  await loadMessages(ticket.id);
  
  if (unreadMessagesCounts[ticket.id] > 0) {
    await markMessagesAsRead(ticket.id);
    await loadUnreadMessagesCounts();
  }
};

  const handleNewTicket = () => {
    setNewTicketDialogOpen(true);
  };

  const handleEditTicketOpen = (ticket: any) => {
    if (authUser?.role === 'customer' && ticket.status_id !== 1) {
      showSnackbar('Talep incelenmeye başlandı, düzenleyemezsiniz. Yeni talep oluşturabilirsiniz.', 'warning');
      return;
    }

    setSelectedTicket(ticket);
    setEditTicketForm({
      module_id: ticket.module_id?.toString() || '',
      subject: ticket.subject || '',
      description: ticket.description || '',
      email: ticket.email || '',
      priority_id: ticket.priority_id?.toString() || '1',
      mail_content: ticket.mail_content || '',
    });
    setEditTicketDialogOpen(true);
    loadTicketFiles(ticket.id);
  };

const handleEdit = (ticket: any) => {
  console.log('Editing ticket:', ticket); // Debug
  
  setSelectedTicket(ticket);
  setEditForm({
    status_id: ticket.status_id?.toString() || '',
    priority_id: ticket.priority_id?.toString() || '',
    assigned_to: ticket.status_id === 1 ? '' : (ticket.assigned_to?.toString() || ''),
    notes: '',
  });
  setEditDialogOpen(true);
};

  const handleDelete = (ticketId: number, ticketSubject?: string) => {
    setSelectedTicket({ 
      id: ticketId, 
      subject: ticketSubject,
      ...tickets.find(t => t.id === ticketId)
    });
    setDeleteDialogOpen(true);
  };

  // File upload handlers
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, existingFilesCount: number = 0) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      const currentTotalFiles = files.length + existingFilesCount;
      
      const oversizedFiles = newFiles.filter(file => file.size > 10 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        showSnackbar('Bazı dosyalar çok büyük (max 10MB)', 'error');
        return;
      }

      if (currentTotalFiles + newFiles.length > 10) {
        const remainingSlots = 10 - currentTotalFiles;
        showSnackbar(`Maksimum 10 dosya yükleyebilirsiniz. ${remainingSlots} dosya daha ekleyebilirsiniz.`, 'error');
        return;
      }

      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearFilters = useCallback(async () => {
    console.log('🧹 Clearing all filters...');
    
    // ✅ useTicketFilters'ten gelen defaultStart ve defaultEnd'yi kullan
    const clearedFilters = {
      status: [],
      module: [],
      priority: [],
      assigned_to: [],
      company: [],
      search: '',
      id_search: '',
      dateRangeCreated: {
        start: defaultStart,
        end: defaultEnd
      },
      dateRangeUpdated: {
        start: defaultStart,
        end: defaultEnd
      }
    };
    
    setFilters(clearedFilters);
    
    // Filtreleri temizledikten sonra tüm talepleri getir
    try {
      setLoading(true);
      const response = await ticketsAPI.getTickets({});
      setTickets(response.data);
      setFilteredTickets(response.data);
    } catch (error: any) {
      console.error('❌ Clear filters error:', error);
      showSnackbar('Talepler yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  }, [defaultStart, defaultEnd, showSnackbar, setFilters]);

  // Effects
  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (authUser) {
      loadUnreadMessagesCounts();
    }
  }, [authUser]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Talepler yükleniyor...</Typography>
      </Box>
    );
  }

  return (
  <Box sx={{ p: 1.5, ml: -1.5 }}> {/* ✅ Padding azaltıldı, daha kompakt */}
      {/* Header ve Filtreler tek satırda */}
      <Box sx={{ mb: 0.1 }}> {/* ✅ mb: 2 → mb: 0.5 daha az boşluk */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 0.5, // ✅ mb: 1 → mb: 0.5 daha kompakt
          flexWrap: 'wrap',
          gap:0.5 // ✅ gap: 1 → gap: 0.5 daha kompakt
        }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 'bold', 
            color: 'primary.main', 
            fontSize: '1.2rem',
            mb: 1 // ✅ Alt boşluk kaldırıldı
          }}>
            📋 {authUser?.role === 'admin' ? 'Tüm Talepler' : 'Taleplerim'} ({filteredTickets.length})
          </Typography>
        </Box>
      </Box>

      {/* Filters - Daha kompakt */}
      <Box sx={{ mb: 1 }}> {/* ✅ mb: 2 → mb: 1 */}
        <TicketFilters
          filters={filters}
          onFilterChange={setFilters}
          onClearFilters={clearFilters}
          onLoadTickets={loadFilteredTickets}
          onNewTicket={handleNewTicket} 
          companies={companies}
          users={users}
          modules={modules}
          statuses={statuses}
          priorities={priorities}
          authUser={authUser}
        />
      </Box>

      {/* Table - Daha kompakt */}
      <Box sx={{ 
        '& .MuiDataGrid-root': {
          fontSize: '0.75rem', // ✅ Daha küçük font
        }
      }}>
        <TicketTable
          tickets={filteredTickets}
          unreadMessagesCounts={unreadMessagesCounts}
          onView={handleView}
          onOpenMessages={handleOpenMessages}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onEditTicketOpen={handleEditTicketOpen}
          onMarkAsRead={markMessagesAsRead}
          authUser={authUser}
          modules={modules}
          priorities={priorities}
          statuses={statuses}
        />
      </Box>

      {/* Dialogs - Artık gerçek bileşenler kullanılıyor */}
      <MessageDialog
        open={messageDialogOpen}
        onClose={() => setMessageDialogOpen(false)}
        selectedTicket={selectedTicket}
        authUser={authUser}
        messages={messages}
        messagesLoading={messagesLoading}
        newMessage={newMessage}
        setNewMessage={setNewMessage}
        messageFile={messageFile}
        setMessageFile={setMessageFile}
        sendingMessage={sendingMessage}
        onSendMessage={handleSendMessage}
        onMessageFileSelect={handleMessageFileSelect}
        onRemoveMessageFile={handleRemoveMessageFile}
        onDownloadMessageFile={handleDownloadMessageFile}
      />

      <DetailViewDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        ticket={selectedTicket}
        authUser={authUser}
        modules={modules}
        priorities={priorities}
        ticketFiles={ticketFiles}
        filesLoading={filesLoading}
        onFilePreview={handleFilePreview}
        onFileDownload={handleFileDownload}
        onEditTicketOpen={handleEditTicketOpen}
        onEdit={handleEdit}
      />

<NewTicketDialog
  open={newTicketDialogOpen}
  onClose={() => {
    setNewTicketDialogOpen(false);
    setFiles([]);
    setMailContent('');
    setNewTicketForm({
      company_id: '',
      module_id: '',
      subject: '',
      description: '',
      email: '',
      priority_id: '1',
    });
  }}
  newTicketForm={newTicketForm}
  setNewTicketForm={setNewTicketForm}
  mailContent={mailContent}
  setMailContent={setMailContent}
  files={files}
  onFileUpload={handleFileUpload}
  onRemoveFile={handleRemoveFile}
onCreateTicket={async () => {
  try {
    console.log('Creating ticket with data:', newTicketForm);
    
    // Müşteri için öncelik ID'sini ayarla
    let finalPriorityId = newTicketForm.priority_id;
    if (authUser?.role === 'customer') {
      finalPriorityId = '1'; // Müşteriler için varsayılan öncelik = Düşük (1)
    }

    // Form verilerini hazırla
const ticketData = {
  ...newTicketForm,
  company_id: authUser?.role === 'customer' ? authUser.companyId : newTicketForm.company_id,
  module_id: parseInt(newTicketForm.module_id),
  // priority_id: Müşteriler için backend sabitliyor, admin için frontend'den geliyor
  priority_id: authUser?.role === 'admin' ? parseInt(newTicketForm.priority_id) : 2,
  mail_content: mailContent
};

    console.log('Sending ticket data:', ticketData);

    // Ticket oluştur
    const response = await ticketsAPI.createTicket(ticketData);
    console.log('Ticket created:', response.data);

    // Dosya yükleme
    if (files.length > 0) {
      console.log('Uploading files...');
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      await ticketsAPI.uploadFiles(response.data.id, formData);
    }

    // Başarı mesajı
    showSnackbar('Talep başarıyla oluşturuldu', 'success');
    
    // Dialog'u kapat ve listeyi yenile
    setNewTicketDialogOpen(false);
    setFiles([]);
    setMailContent('');
    setNewTicketForm({
      company_id: '',
      module_id: '',
      subject: '',
      description: '',
      email: '',
      priority_id: '1',
    });
    
    // Listeyi yenile
    loadFilteredTickets();
    
  } catch (error: any) {
    console.error('Create ticket error:', error);
    
    // Backend'den gelen hatayı göster
    const errorMessage = error.response?.data?.errors?.[0]?.msg || 
                        error.response?.data?.error || 
                        'Talep oluşturulurken hata oluştu';
    
    showSnackbar(errorMessage, 'error');
    throw error;
  }
}}
  authUser={authUser}
  companies={companies}
  modules={modules}
  priorities={priorities}
/>

<EditTicketDialog
  open={editTicketDialogOpen}
  onClose={() => {
    setEditTicketDialogOpen(false);
    setFiles([]);
  }}
  selectedTicket={selectedTicket}
  editTicketForm={editTicketForm}
  setEditTicketForm={setEditTicketForm}
  files={files}
  ticketFiles={ticketFiles}
  filesLoading={filesLoading}
  onFileUpload={handleFileUpload}
  onRemoveFile={handleRemoveFile}
  onFilePreview={handleFilePreview}
  onFileDownload={handleFileDownload}
  onFileDelete={handleFileDelete} // ✅ YENİ: Dosya silme fonksiyonu
  showSnackbar={showSnackbar} // ✅ YENİ: Bildirim fonksiyonu
  onEditTicket={async () => {
    try {
      if (!selectedTicket?.id) {
        showSnackbar('Güncellenecek talep bulunamadı', 'error');
        return;
      }

      // Yeni dosyaları yükle
      if (files.length > 0) {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        await ticketsAPI.uploadFiles(selectedTicket.id, formData);
      }

      // Ticket bilgilerini güncelle - TİP GÜVENLİ VERSİYON
      const updateData: any = {};
      
      // module_id
      if (editTicketForm.module_id && editTicketForm.module_id !== '') {
        updateData.module_id = parseInt(editTicketForm.module_id);
      }
      
      // subject
      if (editTicketForm.subject && editTicketForm.subject !== '') {
        updateData.subject = editTicketForm.subject;
      }
      
      // description
      if (editTicketForm.description && editTicketForm.description !== '') {
        updateData.description = editTicketForm.description;
      }
      
      // email
      if (editTicketForm.email && editTicketForm.email !== '') {
        updateData.email = editTicketForm.email;
      }
      
      // priority_id
      if (editTicketForm.priority_id && editTicketForm.priority_id !== '') {
        updateData.priority_id = parseInt(editTicketForm.priority_id);
      }
      
      // mail_content
      if (editTicketForm.mail_content && editTicketForm.mail_content !== '') {
        updateData.mail_content = editTicketForm.mail_content;
      }

      console.log('Updating ticket with data:', updateData);

      if (Object.keys(updateData).length > 0) {
        await ticketsAPI.editTicket(selectedTicket.id, updateData);
        showSnackbar('Talep başarıyla güncellendi', 'success');
      } else {
        showSnackbar('Değişiklik yapılmadı', 'success')
      }
      
      setEditTicketDialogOpen(false);
      setFiles([]);
      loadFilteredTickets(); // Listeyi yenile
      
    } catch (error: any) {
      console.error('Edit ticket error:', error);
      showSnackbar('Talep güncellenirken hata oluştu: ' + (error.response?.data?.message || error.message), 'error');
      throw error;
    }
  }}
  authUser={authUser}
  modules={modules}
  priorities={priorities}
/>

<EditDialog
  open={editDialogOpen}
  onClose={() => setEditDialogOpen(false)}
  selectedTicket={selectedTicket}
  editForm={editForm}
  setEditForm={setEditForm}
onUpdateTicket={async () => {
  try {
    if (!selectedTicket?.id) {
      showSnackbar('Güncellenecek talep bulunamadı', 'error');
      return;
    }

    console.log('EditForm data:', editForm);
    console.log('Selected ticket:', selectedTicket);

    const updateData: any = {};
    
    // Durum
    if (editForm.status_id && editForm.status_id !== '') {
      updateData.status_id = parseInt(editForm.status_id);
      console.log('Setting status_id:', updateData.status_id);
    }
    
    // Öncelik
    if (editForm.priority_id && editForm.priority_id !== '') {
      updateData.priority_id = parseInt(editForm.priority_id);
      console.log('Setting priority_id:', updateData.priority_id);
    }
    
    // Atanan kullanıcı - backend'in beklediği formata göre
    if (editForm.assigned_to !== undefined && editForm.assigned_to !== '') {
      // Backend user_id mi yoksa assigned_to mu bekliyor kontrol et
      updateData.assigned_to = editForm.assigned_to ? parseInt(editForm.assigned_to) : null;
      // Veya backend user_id bekliyorsa:
      // updateData.user_id = editForm.assigned_to ? parseInt(editForm.assigned_to) : null;
      console.log('Setting assigned_to:', updateData.assigned_to);
    }
    
    // Notlar
    if (editForm.notes) {
      updateData.notes = editForm.notes;
    }

    console.log('Final update data:', updateData);

    if (Object.keys(updateData).length > 0) {
      // Eğer sadece atama işlemiyse assignTicket kullan
      if (updateData.assigned_to !== undefined && Object.keys(updateData).length === 1) {
        console.log('Using assignTicket API');
        await ticketsAPI.assignTicket(selectedTicket.id, { user_id: updateData.assigned_to });
      } else {
        console.log('Using updateTicket API');
        await ticketsAPI.updateTicket(selectedTicket.id, updateData);
      }
      
      showSnackbar('Talep başarıyla güncellendi', 'success');
      
      setEditDialogOpen(false);
      setSelectedTicket(null);
      loadFilteredTickets(); // Listeyi yenile
    } else {
      showSnackbar('Değişiklik yapılmadı', 'success')
    }
    
  } catch (error: any) {
    console.error('Update ticket error:', error);
    console.error('Error details:', error.response?.data);
    showSnackbar('Talep güncellenirken hata oluştu: ' + (error.response?.data?.message || error.message), 'error');
    throw error;
  }
}}
  authUser={authUser}
  statuses={statuses}
  priorities={priorities}
  users={users}
/>

<DeleteDialog
  open={deleteDialogOpen}
  onClose={() => {
    setDeleteDialogOpen(false);
    setSelectedTicket(null);
  }}
  selectedTicket={selectedTicket}
  onConfirmDelete={async () => {
    try {
      if (!selectedTicket?.id) {
        showSnackbar('Silinecek talep bulunamadı', 'error');
        return;
      }

      await ticketsAPI.deleteTicket(selectedTicket.id);
      showSnackbar('Talep başarıyla silindi', 'success');
      
      setDeleteDialogOpen(false);
      setSelectedTicket(null);
      loadFilteredTickets(); // Listeyi yenile
      
    } catch (error: any) {
      console.error('Delete ticket error:', error);
      showSnackbar('Talep silinirken hata oluştu: ' + (error.response?.data?.message || error.message), 'error');
    }
  }}
/>

      <SuccessDialog
        open={successDialog}
        onClose={() => {
          setSuccessDialog(false);
          loadFilteredTickets();
        }}
        selectedTicket={selectedTicket}
      />

      <UpdateSuccessDialog
        open={updateSuccessDialog}
        onClose={() => setUpdateSuccessDialog(false)}
        selectedTicket={selectedTicket}
        onView={() => {
          setUpdateSuccessDialog(false);
          handleView(selectedTicket);
        }}
      />

      <FilePreviewDialog
        previewFile={previewFile}
        onPreviewClose={handlePreviewClose}
        previewLoading={previewLoading}
        onFileDownload={handleFileDownload}
      />

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={3000} 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TicketList;